import React from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import {
  CreditCard, Globe, Package, ArrowRight, Settings, ExternalLink,
  RefreshCw, BarChart3, CheckCircle, XCircle, Loader2, Copy,
} from 'lucide-react';

const API_URL = window.location.origin;

export const AdminPayFastTab = ({
  allVendors,
  recentTransactions,
  loadingTransactions,
  fetchRecentTransactions,
  fetchVendors,
  token,
}) => {
  return (
    <>
      <div className="bg-white dark:bg-[#1a1a1a] rounded-xl border-2 border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-xl font-bold text-[#252525] dark:text-white flex items-center gap-2 mb-6">
          <CreditCard className="w-6 h-6 text-[#FFD12D]" /> PayFast Configuration
        </h3>

        {/* Status Badge */}
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-green-700 dark:text-green-400 font-medium">PayFast Integration Active</span>
          </div>
          <p className="text-sm text-green-600 dark:text-green-500 mt-1">Your payment gateway is configured and ready to accept subscriptions.</p>
        </div>

        {/* Configuration Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="font-semibold text-[#252525] dark:text-white flex items-center gap-2"><Globe className="w-4 h-4 text-[#FFD12D]" /> Webhook URLs</h4>
            <div className="space-y-3">
              {[
                { label: 'ITN Notify URL (Recurring Billing)', url: 'https://wayo.co.za/api/payfast/notify' },
                { label: 'Subscription Notify URL', url: 'https://wayo.co.za/api/payfast/subscription-notify' },
              ].map(({ label, url }) => (
                <div key={label} className="p-3 bg-gray-50 dark:bg-[#252525] rounded-lg">
                  <Label className="text-xs text-gray-500 dark:text-gray-400">{label}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 text-sm text-[#252525] dark:text-white bg-gray-100 dark:bg-[#1a1a1a] px-2 py-1 rounded break-all">{url}</code>
                    <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => { navigator.clipboard.writeText(url); toast.success('URL copied!'); }}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-[#252525] dark:text-white flex items-center gap-2"><Package className="w-4 h-4 text-[#FFD12D]" /> Subscription Plans</h4>
            <div className="space-y-3">
              <div className="p-4 bg-gray-50 dark:bg-[#252525] rounded-lg border-2 border-gray-200 dark:border-gray-600">
                <div className="flex justify-between items-start">
                  <div><h5 className="font-medium text-[#252525] dark:text-white">Monthly Plan</h5><p className="text-sm text-gray-500 dark:text-gray-400">Billed monthly</p></div>
                  <span className="text-xl font-bold text-[#252525] dark:text-white">R25<span className="text-sm font-normal text-gray-500">/mo</span></span>
                </div>
              </div>
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border-2 border-green-500">
                <div className="flex justify-between items-start">
                  <div>
                    <h5 className="font-medium text-[#252525] dark:text-white flex items-center gap-2">
                      Yearly Plan
                    </h5>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Billed annually</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xl font-bold text-[#252525] dark:text-white">R300<span className="text-sm font-normal text-gray-500">/yr</span></span>
                    <p className="text-xs text-green-600">R25/month</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Flow */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <h4 className="font-semibold text-[#252525] dark:text-white flex items-center gap-2 mb-4"><ArrowRight className="w-4 h-4 text-[#FFD12D]" /> Payment Flow</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { step: '1', title: 'Vendor Selects Plan', desc: 'Chooses monthly or yearly' },
              { step: '2', title: 'Redirect to PayFast', desc: 'Secure payment page' },
              { step: '3', title: 'Payment Processed', desc: 'PayFast handles transaction' },
              { step: '4', title: 'Subscription Active', desc: 'Stall can be published' },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center text-center p-3 bg-gray-50 dark:bg-[#252525] rounded-lg">
                <div className="w-8 h-8 bg-[#FFD12D] text-[#252525] rounded-full flex items-center justify-center font-bold mb-2">{item.step}</div>
                <h5 className="font-medium text-sm text-[#252525] dark:text-white">{item.title}</h5>
                <p className="text-xs text-gray-500 dark:text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <h4 className="font-semibold text-[#252525] dark:text-white flex items-center gap-2 mb-4"><Settings className="w-4 h-4 text-[#FFD12D]" /> Quick Actions</h4>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => window.open('https://www.payfast.co.za/login', '_blank')} className="flex items-center gap-2">
              <ExternalLink className="w-4 h-4" /> PayFast Dashboard
            </Button>
            <Button variant="outline" onClick={async () => {
              try {
                const response = await fetch(`${API_URL}/api/admin/trial-check`, { method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
                if (response.ok) toast.success('Trial check completed');
              } catch { toast.error('Failed to run trial check'); }
            }} className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4" /> Run Trial Check
            </Button>
          </div>
        </div>
      </div>

      {/* Subscription Summary */}
      <div className="bg-white dark:bg-[#1a1a1a] rounded-xl border-2 border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-[#FFD12D]" /> Subscription Summary
          <span className="text-sm font-normal text-gray-500 dark:text-gray-400">(Approved Vendors Only)</span>
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'On Trial', filter: v => v.subscription === 'trial', color: 'blue' },
            { label: 'Monthly', filter: v => v.subscription === 'monthly', color: 'green' },
            { label: 'Yearly', filter: v => v.subscription === 'yearly', color: 'purple' },
            { label: 'Cash', filter: v => v.subscription === 'cash' || !v.subscription, color: 'yellow' },
            { label: 'Manual', filter: v => v.subscription === 'manual', color: 'orange' },
          ].map(({ label, filter, color }) => (
            <div key={label} className={`p-4 bg-${color}-50 dark:bg-${color}-900/20 rounded-lg text-center`}>
              <p className={`text-2xl font-bold text-${color}-600 dark:text-${color}-400`}>
                {allVendors.filter(v => v.role === 'vendor' && v.status === 'approved' && filter(v)).length}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white dark:bg-[#1a1a1a] rounded-xl border-2 border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#FFD12D]" /> Recent Transactions
          </h3>
          <Button size="sm" variant="outline" onClick={fetchRecentTransactions} disabled={loadingTransactions} className="flex items-center gap-1">
            <RefreshCw className={`w-4 h-4 ${loadingTransactions ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {loadingTransactions ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-[#FFD12D]" />
            <span className="ml-2 text-gray-500">Loading transactions...</span>
          </div>
        ) : recentTransactions.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <CreditCard className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>No transactions yet</p>
            <p className="text-sm">Transactions will appear here when vendors subscribe</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  {['Date', 'User', 'Company', 'Plan', 'Amount', 'Status', 'Actions'].map(h => (
                    <th key={h} className={`${h === 'Amount' ? 'text-right' : h === 'Status' || h === 'Actions' ? 'text-center' : 'text-left'} py-3 px-2 font-semibold text-gray-600 dark:text-gray-400`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recentTransactions.map((tx, index) => (
                  <tr key={tx.id || index} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="py-3 px-2 text-gray-600 dark:text-gray-300">
                      {tx.created_at ? new Date(tx.created_at).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </td>
                    <td className="py-3 px-2">
                      <p className="font-medium text-[#252525] dark:text-white">{tx.user_name}</p>
                      <p className="text-xs text-gray-500">{tx.user_email}</p>
                    </td>
                    <td className="py-3 px-2 text-gray-600 dark:text-gray-300">{tx.company_name || '-'}</td>
                    <td className="py-3 px-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        tx.plan === 'monthly' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        tx.plan === 'yearly' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                        'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                      }`}>{tx.plan ? tx.plan.charAt(0).toUpperCase() + tx.plan.slice(1) : '-'}</span>
                    </td>
                    <td className="py-3 px-2 text-right font-medium text-[#252525] dark:text-white">R{tx.amount ? (tx.amount / 100).toFixed(2) : '0.00'}</td>
                    <td className="py-3 px-2 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        tx.status === 'complete' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        tx.status === 'pending' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                        tx.status === 'cancelled' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                        'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                      }`}>
                        {tx.status === 'complete' ? '\u2713 Complete' : tx.status === 'pending' ? '\u23F3 Pending' : tx.status === 'cancelled' ? '\u2715 Cancelled' : tx.status}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center">
                      {tx.status === 'pending' && (
                        <Button size="sm" onClick={async () => {
                          try {
                            const response = await fetch(`${API_URL}/api/payfast/verify-payment/${tx.id}`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
                            if (response.ok) { toast.success('Payment verified! User subscription updated.'); fetchRecentTransactions(); fetchVendors(); }
                            else { const error = await response.json(); toast.error(error.detail || 'Failed to verify payment'); }
                          } catch { toast.error('Failed to verify payment'); }
                        }} className="bg-green-500 hover:bg-green-600 text-white text-xs px-2 py-1 h-7">
                          <CheckCircle className="w-3 h-3 mr-1" /> Verify
                        </Button>
                      )}
                      {tx.status === 'complete' && (
                        <Button size="sm" variant="outline" onClick={async () => {
                          if (!window.confirm('Are you sure you want to undo this verification? The user subscription will be reverted.')) return;
                          try {
                            const response = await fetch(`${API_URL}/api/payfast/undo-verify/${tx.id}`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
                            if (response.ok) { toast.success('Payment verification reverted.'); fetchRecentTransactions(); fetchVendors(); }
                            else { const error = await response.json(); toast.error(error.detail || 'Failed to undo verification'); }
                          } catch { toast.error('Failed to undo verification'); }
                        }} className="border-orange-400 text-orange-600 hover:bg-orange-50 text-xs px-2 py-1 h-7">
                          <XCircle className="w-3 h-3 mr-1" /> Undo
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};
